// src/services/prendasService.jsx

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// ============================================================
// FORMATEADORES
// ============================================================

const formatGrupo = (grupo) => String(grupo).trim();

const formatOrden = (orden) => String(orden).trim();

/**
 * Formatear grupo para Tracking
 * Ejemplo:
 * 1      -> 000001
 * 123    -> 000123
 * 000001 -> 000001
 */
const formatearGrupoTracking = (grupo) => {
  return String(grupo).trim().padStart(6, '0');
};

/**
 * Formatear orden para Tracking
 * Ejemplo:
 * 1    -> 0001
 * 12   -> 0012
 * 0001 -> 0001
 */
const formatearOrdenTracking = (orden) => {
  return String(orden).trim().padStart(4, '0');
};

// ============================================================
// OBTENER LISTADO DE PRENDAS DISPONIBLES
// ============================================================

/**
 * Obtener listado de prendas disponibles (sin parámetros)
 * Endpoint: POST /api/Prendas/getPrendasInformation
 */
export const getPrendasInformationList = async () => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Prendas/getPrendasInformation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({}),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al obtener listado: ${response.status}`
      );
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      console.warn(
        '[prendasService] La respuesta no es un array:',
        data
      );

      return [];
    }

    return data;
  } catch (error) {
    console.error(
      '[prendasService] Error en getPrendasInformationList:',
      error
    );

    throw error;
  }
};

// ============================================================
// OBTENER INFORMACIÓN DE UNA PRENDA
// ============================================================

/**
 * Obtener información de una prenda puntual
 * Endpoint: POST /api/Prendas/getPrendasInformation
 *
 * Body:
 * {
 *   group,
 *   order
 * }
 */
export const getPrendasInformation = async (
  group,
  order,
  sellerID
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Prendas/getPrendasInformation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          group: formatGrupo(group),
          order: formatOrden(order),
          sellerID: sellerID
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al obtener prenda: ${response.status}`
      );
    }

    const data = await response.json();

    if (data.errMsg) {
      throw new Error(data.errMsg);
    }

    return data;
  } catch (error) {
    console.error(
      '[prendasService] Error en getPrendasInformation:',
      error
    );

    throw error;
  }
};

// ============================================================
// OBTENER INFORMACIÓN DE PRENDA DISPONIBLE
// ============================================================

/**
 * Obtener información de una prenda disponible
 * Endpoint: POST /api/Prendas/getPrendasDisponibleInformation
 */
export const getPrendasDisponibleInformation = async (
  group,
  order,
  sellerID
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Prendas/getPrendasDisponibleInformation`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          group: formatGrupo(group),
          order: formatOrden(order),
          sellerID: sellerID
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al obtener prenda disponible: ${response.status}`
      );
    }

    const data = await response.json();

    if (data.errMsg) {
      throw new Error(data.errMsg);
    }

    return data;
  } catch (error) {
    console.error(
      '[prendasService] Error en getPrendasDisponibleInformation:',
      error
    );

    throw error;
  }
};

// ============================================================
// OBTENER LISTADO DE PRENDAS POR ESTADO
// ============================================================

/**
 * Obtener listado de prendas por estado
 *
 * Endpoint:
 * GET /api/Prendas/getPrendasListing
 *
 * Estados posibles:
 * DISPONIBLES
 * INCOMPLETOS
 * RECHAZADOS
 * EN REVISION
 */
export const getPrendasListing = async (prendaStatus, sellerID) => {

  try {

    const url = `${API_BASE_URL}/api/Prendas/getPrendasListing?prendaStatus=${encodeURIComponent(prendaStatus)}&sellerID=${encodeURIComponent(sellerID)}`

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response
        .text()
        .catch(() => '');

      throw new Error(
        `Error al obtener listado de prendas: ${response.status} - ${errorText}`
      );
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      console.warn(
        '[prendasService] La respuesta no es un array:',
        data
      );

      return [];
    }

    return data;
  } catch (error) {
    console.error(
      '[prendasService] Error en getPrendasListing:',
      error
    );

    throw error;
  }
};

// ============================================================
// OBTENER TRACKING DE UNA PRENDA
// ============================================================

/**
 * Obtener tracking de eventos de una prenda
 *
 * Endpoint:
 * GET /api/Prendas/getPrendasTracking
 *
 * Parámetros esperados por el backend:
 *
 * ?grupo=000001&orden=0001
 *
 * @param {string|number} group - Número de grupo
 * @param {string|number} order - Número de orden
 * @returns {Promise<Array>} Array de eventos del tracking
 */
export const getPrendasTracking = async (
  group,
  order,
  sellerID
) => {
  try {
    // ========================================================
    // FORMATEAR GRUPO Y ORDEN
    // ========================================================

    const grupoFormateado =
      formatearGrupoTracking(group);

    const ordenFormateada =
      formatearOrdenTracking(order);

    console.log(
      '[prendasService] Buscando tracking:',
      {
        grupoOriginal: group,
        ordenOriginal: order,
        grupoFormateado,
        ordenFormateada,
      }
    );

    // ========================================================
    // ARMAR URL
    //
    // IMPORTANTE:
    // El backend espera "grupo" y "orden"
    // NO "group" y "order"
    // ========================================================

    const url =
      `${API_BASE_URL}/api/Prendas/getPrendasTracking` +
      `?grupo=${encodeURIComponent(grupoFormateado)}` +
      `&orden=${encodeURIComponent(ordenFormateada)}` + 
      `&sellerID=${encodeURIComponent(sellerID)}`;

    console.log(
      '[prendasService] URL Tracking:',
      url
    );

    // ========================================================
    // REALIZAR REQUEST
    // ========================================================

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    // ========================================================
    // VALIDAR RESPUESTA HTTP
    // ========================================================

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      console.error(
        '[prendasService] Error HTTP Tracking:',
        {
          status: response.status,
          errorData,
        }
      );

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al obtener tracking: ${response.status}`
      );
    }

    // ========================================================
    // OBTENER RESPUESTA
    //
    // El endpoint devuelve directamente un ARRAY:
    //
    // [
    //   {
    //     group: "000001",
    //     order: "0001",
    //     eventDescription: "EN REVISION",
    //     userName: "PEDRO GOMEZ",
    //     notes: "",
    //     eventDate: "2026-07-22 08:47:02"
    //   }
    // ]
    // ========================================================

    const data = await response.json();

    console.log(
      '[prendasService] Respuesta Tracking:',
      data
    );

    // ========================================================
    // VALIDAR ERROR DEVUELTO POR EL BACKEND
    // ========================================================

    if (data?.errMsg) {
      throw new Error(data.errMsg);
    }

    // ========================================================
    // DEVOLVER ARRAY DE EVENTOS
    // ========================================================

    return data;
  } catch (error) {
    console.error(
      '[prendasService] Error en getPrendasTracking:',
      error
    );

    throw error;
  }
};

// ============================================================
// CAMBIAR ESTADO DE UNA PRENDA
// ============================================================

/**
 * Cambiar estado de una prenda
 *
 * Endpoint:
 * POST /api/Prendas/setPrendasStatus
 *
 * @param {number} idPrenda
 * @param {string} prendaStatus
 * @param {string} statusNotes
 * @param {number} userID
 */
export const setPrendasStatus = async (
  idPrenda,
  prendaStatus,
  statusNotes = '',
  userID = 0
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Prendas/setPrendasStatus`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          idPrenda,
          prendaStatus,
          statusNotes,
          userID,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al cambiar estado: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(
      '[prendasService] Error en setPrendasStatus:',
      error
    );

    throw error;
  }
};

// ============================================================
// GUARDAR / ACTUALIZAR INFORMACIÓN DE PRENDA
// ============================================================

/**
 * Guardar/actualizar información de una prenda
 *
 * Endpoint:
 * POST /api/Prendas/storePrendas
 *
 * @param {Object} prendaData
 */
export const storePrendas = async (
  prendaData
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Prendas/storePrendas`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(prendaData),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al guardar prenda: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(
      '[prendasService] Error en storePrendas:',
      error
    );

    throw error;
  }
};

// ============================================================
// CONVERTIR ARCHIVO A BASE64
// ============================================================

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(
        reader.result.split(',')[1]
      );
    };

    reader.onerror = (err) => {
      reject(err);
    };

    reader.readAsDataURL(file);
  });

// ============================================================
// SUBIR DOCUMENTO DE UNA PRENDA
// ============================================================

/**
 * Subir documento de una prenda
 *
 * Endpoint:
 * POST /api/Prendas/uploadPrendasDocument
 *
 * @param {string|number} idPrenda
 * @param {string} documentName
 * @param {File} file
 */
export const uploadPrendasDocument = async (
  idPrenda,
  documentName,
  file
) => {
  try {
    const documentB64 =
      await fileToBase64(file);

    const response = await fetch(
      `${API_BASE_URL}/api/Prendas/uploadPrendasDocument`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          idPrenda: String(idPrenda),
          documentName,
          documentB64,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al subir documento: ${response.status}`
      );
    }

    return await response.json();
  } catch (error) {
    console.error(
      '[prendasService] Error en uploadPrendasDocument:',
      error
    );

    throw error;
  }
};

// ============================================================
// RESET DE UNA PRENDA
// ============================================================

/**
 * Resetear una prenda
 *
 * Endpoint:
 * POST /api/Prendas/resetCase
 *
 * @param {string} group
 * @param {string} order
 */
export const resetPrendaCase = async (
  group,
  order
) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/api/Prendas/resetCase`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          group: String(group),
          order: String(order),
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      throw new Error(
        errorData.errMsg ||
          errorData.message ||
          `Error al resetear prenda: ${response.status}`
      );
    }

    return await response
      .json()
      .catch(() => ({}));
  } catch (error) {
    console.error(
      '[prendasService] Error en resetPrendaCase:',
      error
    );

    throw error;
  }
};